import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { IkDomElement, IkDomNode, kHtmlParserCore } from "../../../Admin/kReusableCoreTS/kHtmlParserCore";
import { kHtmlSafeCore, kHtmlSafeResult } from "../../../Admin/kReusableCoreTS/kHtmlSafeCore";
import { AppAiChat, AppAiChatDoc } from "./AppAiChat";
import { AppAssistAnswer, AppAssistApi, AppAssistTurn } from "./AppAssistApi";
import { AppAssistExport } from "./AppAssistExport";
import { AppAssistRetry } from "./AppAssistRetry";
import { AppAssistForget, AppAssistForgetCall } from "./AppAssistForget";
import { AppAssistKeep, AppAssistKeepCall } from "./AppAssistKeep";
import { AppAssistRich } from "./AppAssistRich";
import { AppAssistCitations, AppCitationPart } from "./AppAssistCitations";
import { AppPins } from "./AppPins";
import { AppAssistBlock, AppAssistEntry, AppAssistRules, AppAssistSession, AppAssistSpan } from "./AppAssistRules";
import { AppAssistView } from "./AppAssistView";
import { AppIcons, IIconDoc, IIconNode } from "./AppIcons";
import { AppAssistStream, AppAssistStreamPart, AppAssistStreamRich } from "./AppAssistStream";
import { IComposerSent } from "./AppComposer";

// AppAssistPanel - "ask AI about this chat": asks `/assist`, draws the question-and-answer log for
// one contact, and puts each draft on a card with Send, Edit and Copy. `AppAssistRules` decides;
// this is the act.
//
// ⭐ THE CARD'S SEND SENDS (owner, 2026-09-25: "The Use button in the AI chat should send right
// away"). ⛔ DECISION 9 STILL HOLDS, AND THIS IS HOW: the ONLY call to `IAssistHost.Send` is in
// `Act("send")`, i.e. the owner's click on a draft they can see. An arriving answer (`AppAssistCall`),
// a render, Enter in the question box - none of them can reach it. The host's `Send` is the
// composer's own `AppComposer.SendText`: the same `/send` request, validation, bubble and
// one-at-a-time latch as the Send button under the chat. ⛔ Never a second sender.
// ⛔ ONE CLICK, ONE MESSAGE: the entry is marked `Sending` before the host is called, and
// `AppAssistRules.CanSend` refuses a card that is sending or sent - with the button disabled too.
// ⚠ Edit (`IAssistHost.Fill`) is the old "Use this draft": into the box, focused, nothing sent.
// ⛔ THE MODEL'S WORDS ARE `textContent`, NEVER MARKUP: an answer that contains `<img onerror>` is
// shown as those characters. Its simple Markdown (`AppAssistRules.Blocks`) becomes ELEMENTS whose
// text is set as text - `<strong>`, `<em>`, `<code>`, `<ul>`/`<ol>`, `<p>`, `<br>`, and a pipe table's
// `<table>` rows and cells.
// ⛔ TABLES AND CHARTS (a ```wac-html fence, `AppAssistRich`) ARE NEVER HANDED TO THE BROWSER AS MARKUP.
// This panel shares its page with the `browser` archive key, which can SEND as the owner, and a
// contact's messages can steer the model. So: the shared pure parser + allow-list
// (`kHtmlSafeCore.Clean`) first, then every survivor BUILT - `createElement`/`createElementNS`, each
// attribute by `setAttribute`, words by `createTextNode`. No `innerHTML`, `outerHTML`,
// `insertAdjacentHTML` or `DOMParser` in this file - `AppAssistRich.test.ts` greps for them.
// ⚠ A REFUSAL IS A SENTENCE IN INK (law 1: validation), never the error handler and never orange:
// "AI help isn't available yet" is the product working as configured.
// ⚠ PRIVATE DATA: no question, answer or draft is ever written to the console.
// ⚠ SHAPED BY INTERFACES, like `AppChatAppend`, so the suite drives it with a small fake document.
//
// ⭐ THE HISTORY IS ALWAYS THERE (owner, 2026-09-26: "AI chat history should be persisted and always visible
// - per contact person"). `LoadHistory` reads the contact's `ai-chat.json` (the owner's own Dropbox, the read
// ticket) and draws it above this visit's questions, oldest first, a line for each day, scrolled to the end.
// A missing file (Dropbox's 409 `path/not_found`) is simply no history yet; an unreadable one is one ink
// sentence and no history. ⛔ Only the newest draft keeps Send and Edit, and only while nothing has been
// done with it (`AppAssistRules.CanSendAt`); what the owner does with a draft - Send, Edit - is recorded
// on its item (`IAssistHost.Outcome`), so after a reload a draft that went is never offered again.
//
// ⭐ A TABLE OR A CHART CAN BE TAKEN OUT (2026-09-27, `AppAssistExport` rules, `AppAssistTools` browser half).
// Under each table a small "Copy table" (tab-separated, header first - pastes into a spreadsheet); under each
// chart "Save chart" (an .svg of the chart as BUILT here, colours written onto it); a tap, a click, Enter or
// focus on a bar that has a `<title>` shows that title under the chart, a tap elsewhere hides it. Each block
// has its own note line, changed IN PLACE - a repaint would scroll the log to its end under the owner's
// finger. The blocks and marks are numbered afresh on every paint (`Exports`, `Marks`), and so are the
// buttons that point at them. ⛔ None of it sends anything, and none of it is markup: the rows are read from
// the CLEANED tree, the saved file is serialised from our own built nodes.
// ⚠ A REFUSED CLIPBOARD OR DOWNLOAD IS A SENTENCE IN INK (fate 1): it is the browser's answer - a
// permission, an insecure context, an old browser - and the owner can do it by hand. Only a fault in our
// own code goes to the handler.
//
// ⭐ A SAVED QUESTION CAN BE REMOVED (2026-10-01, `AppAssistForget`). Under each entry that is in the saved history
// (owner's browser, never in a mode, never the question in flight) a small bin, "Remove from history". Its click
// shows "Remove this answer?" with Remove and Cancel in the entry; only that Remove asks the service
// (`/assist {action:"forget", id}`), the entry fades until the answer, a refusal puts it back with the service's
// sentence in ink. These repaints keep the log where the owner is reading (`RenderInPlace`).
// ⛔ IT NEVER SENDS, AND IT NEVER UNCOVERS A SEND: an older draft does not become sendable because a newer turn was
// removed (`AppAssistRules.Retire`), and for `AppAssistForget.SendGuardMs` after an entry left the log a click on
// a draft's Send does nothing - the log has just moved under the pointer.

/** A text node - what `createTextNode` makes. ⛔ Its words are text, whatever they spell. */
export interface IAssistText {
    textContent: string | null;
}

export interface IAssistNode extends IAssistText {
    className: string;
    scrollTop: number;
    scrollHeight: number;
    setAttribute(Name: string, Value: string): void;
    getAttribute(Name: string): string | null;
    appendChild(Child: IAssistNode | IAssistText): unknown;
    /** The browser's own (optional - a fake document has none): bring the node into its scroller's view. */
    scrollIntoView?(Options?: { block?: string }): void;
    /** The browser's own (optional): the keyboard focus - "Remove from history" keeps it on the control it drew. */
    focus?(): void;
}

export interface IAssistDoc {
    /** The browser's own (optional): where the keyboard focus is, and the body it falls back to when its element is redrawn. */
    activeElement?: unknown;
    body?: unknown;
    createElement(Tag: string): IAssistNode;
    /** For a chart's SVG elements (`kHtmlSafeCore.SvgNs`). ⚠ Never set `className` on one - it is read-only there. */
    createElementNS(Ns: string, Tag: string): IAssistNode;
    createTextNode(Text: string): IAssistText;
}

export type AppAssistDone = (Status: number, Text: string) => void;

/** What the panel may ask of the page around it. */
export interface IAssistHost {
    /** POST the body to `/assist`; `Done` is answered exactly once, (0, "") for a lost connection. */
    Post(Body: string, Done: AppAssistDone): void;
    /**
     * ⭐ POST a question asking for a STREAMED answer (`AppAssistStream`): `OnText` hears the answer so far
     * (already `Visible`) as it grows, `Done` the final one-object answer exactly once. Optional - a host
     * without it gets the whole answer at the end through `Post`.
     */
    PostStream?(Body: string, OnText: (Partial: string, Rich?: AppAssistStreamRich[]) => void, Done: AppAssistDone): void;
    /**
     * ⛔ The composer's own send (`AppComposer.SendText`) - "" when the request went (`Then` is then
     * answered once), else the sentence to show. Called ONLY from the card's Send click.
     */
    Send(Text: string, Then: IComposerSent): string;
    /** Edit: put text into the composer and focus it. Sends nothing. */
    Fill(Text: string): void;
    /** Put text on the clipboard; the host says how it went through `Say`. */
    Copy(Text: string): void;
    /** An answer was recorded into `Session` - repaint whichever panel shows it now. */
    Answered(Session: AppAssistSession): void;
    /**
     * Record what the owner did with the draft on history item `Id` (`sent`, `edited`). ⛔ It RECORDS -
     * nothing is sent by it. Fire and forget: the entry already shows the outcome on this visit.
     */
    Outcome(Id: string, Outcome: string): void;
    /**
     * Read this contact's `ai-chat.json`: `Done` with its text, `Missing` when there is none yet (409
     * `path/not_found`), `Fail` with a sentence otherwise - exactly one of them, once.
     */
    ReadHistory(Done: (Text: string) => void, Missing: () => void, Fail: (Why: string) => void): void;
    /**
     * ⭐ Run `Then` once after `Ms` (the browser's `setTimeout`) - how a refusal that clears on its own (`retry`, e.g. the
     * folder not restored yet after a deploy) is asked again. Optional: a host without it shows the refusal as before.
     */
    Later?(Ms: number, Then: () => void): void;
    /**
     * ⭐ A "Remove from history" ended (`AppAssistForget`): `Removed` true = the turn is out of the saved history
     * (tell the other tabs, update the ✦ count), false = refused, the entry is back. Repaint in place
     * (`AppAssistPanel.RenderInPlace`). Optional: a host without it hears `Answered`.
     */
    Forgot?(Session: AppAssistSession, Removed: boolean): void;
}

/** What the log's own listeners (`IAssistTools.Listen`) ask of the panel: a chart mark was chosen, or not. */
export interface IAssistMarks {
    /** Show mark `N`'s `<title>` under its chart (a tap, a click, Enter, focus). */
    ShowMark(N: number): void;
    /** A tap anywhere else in the log, or Escape: every chart's value line empties. */
    HideMarks(): void;
    /**
     * Escape in the log: an open "Remove this answer?" is taken back (as its Cancel does). True = one was open, and
     * the key is spent on it (the listener stops it there, so the same Esc does not also close the AI sheet).
     */
    CloseConfirm?(): boolean;
}

/**
 * One piece of an answer's words (`IAssistMode.Pieces`): plain text, or - when `Href` is set - a link to one of
 * the owner's OWN chats, drawn as `<a href="#c/...">` with `Label` as its accessible name. ⛔ `Href` is only ever
 * a row of the page's own index (the mode checks); never a free address.
 */
export class AppAssistPiece {
    public Text: string = "";
    /** The chat's index href (raw, as `index.json` writes it), or "" for plain text. */
    public Href: string = "";
    /** The contact's name, for the link's accessible name. */
    public Label: string = "";
}

/**
 * ⭐ A PANEL MODE (2026-09-27, "Ask about everyone" - `AppAskAll`): the same log, rendering, rich blocks and
 * stream, over a different question. A panel given a mode asks with the mode's body, shows the mode's intro,
 * turns its words into pieces (links to chats), and ⛔ NEVER DRAWS A DRAFT - a mode panel has no Send, Edit or
 * draft card at all (decision 9: nothing sends from a panel that is not about one chat).
 */
export interface IAssistMode {
    /** The request body for `Question` with the earlier `Turns`, or "" (a signed-out browser). */
    Body(Question: string, Turns: AppAssistTurn[]): string;
    /** The line an empty log shows. */
    Intro(): string;
    /** One run of answer text as pieces - plain text and links. ⛔ Text stays text. */
    Pieces(Text: string): AppAssistPiece[];
}

/** How one "Copy table" went - answered once. */
export interface IAssistCopied {
    Copied(): void;
    /** The browser would not copy (no permission, no clipboard) - a sentence in ink, never the handler. */
    Refused(): void;
}

/** The browser's half of taking a block out (`AppAssistTools`); a fake in the suite. ⛔ Nothing here sends. */
export interface IAssistTools {
    /** Listen on the log for taps, keys and focus on chart marks - ONCE per log element, however often it is called. */
    Listen(Log: IAssistNode, Marks: IAssistMarks): void;
    /** Put `Text` on the clipboard (the fallback too); `Then` is answered once. */
    Copy(Text: string, Then: IAssistCopied): void;
    /** Download the built chart as `Name`: "" when the download started, else the sentence to show. */
    Save(Chart: IAssistNode, Name: string): string;
}

/**
 * A table or chart drawn in this paint - what its button points at. `Rows` for a table; `Chart` (the built
 * outer `<svg>`), `Value` (the tapped bar's line) and `Seq` (1 for the log's first chart) for a chart.
 */
export class AppAssistBlockOut {
    public Kind: string = "";
    public Rows: string[][] = [];
    public Chart: IAssistNode | null = null;
    public Value: IAssistNode | null = null;
    /** The block's own note line ("Copied - …", "Saved as …"), changed in place. */
    public Note: IAssistNode | null = null;
    public Seq: number = 0;
    /** "Keep this" (`AppAssistKeep`): the saved answer's item id ("" = not keepable) and its block number. */
    public Item: string = "";
    public Block: number = -1;
    public Keep: IAssistNode | null = null;
}

/** A chart mark with a `<title>`: which block it is in, its words, and the built node. */
export class AppAssistMark {
    public Block: number = -1;
    public Text: string = "";
    public Node: IAssistNode | null = null;
}

/**
 * The tools of a panel given none (a habitat without a browser): listens to nothing, and a copy or a save is
 * refused with the same ink sentence a refusing browser gets. ⛔ Habitat-free on purpose - see the constructor.
 */
export class AppAssistNoTools implements IAssistTools {
    public Listen = (Log: IAssistNode, Marks: IAssistMarks): void => {
        // DOCUMENTED EXCEPTION: nothing to listen with - a deliberate no-op, cannot throw.
    };

    public Copy = (Text: string, Then: IAssistCopied): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            Then.Refused();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistNoTools.Copy");
        }
    };

    /** DOCUMENTED EXCEPTION: pure constant answer - no input used, cannot throw. */
    public Save = (Chart: IAssistNode, Name: string): string => {
        return AppAssistExport.SaveRefusedNote();
    };
}

/** One "Copy table" in flight: the answer lands on that table's own note line. */
export class AppAssistTableCopy implements IAssistCopied {
    private Note: IAssistNode;

    public constructor(Note: IAssistNode) {
        this.Note = Note;
    }

    public Copied = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.Note.textContent = AppAssistExport.CopiedNote();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistTableCopy.Copied");
        }
    };

    /** ERROR-SUPPRESSED-JUSTIFIED: the browser refusing the clipboard is its answer; the note says what to do. */
    public Refused = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.Note.textContent = AppAssistExport.CopyRefusedNote();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistTableCopy.Refused");
        }
    };
}

/** Who the panel is asking for. ⚠ `Key` and `Phone` are the archive proof `/send` uses. */
export class AppAssistWho {
    public Phone: string = "";
    public Key: string = "";
    /** The contact's href exactly as the route and the index carry it. */
    public Page: string = "";
}

/**
 * One ask in flight. It carries its session and `Seq`, so an answer lands in the conversation it
 * was asked in - even after the owner moved to another contact - and is dropped if it was cancelled.
 */
export class AppAssistCall {
    private Session: AppAssistSession;
    private Seq: number;
    private Host: IAssistHost;
    /** The body this ask posted, and how - so a refusal that clears on its own is asked again with the SAME question. */
    public Body: string = "";
    public Streamed: boolean = false;
    /** How many times it was asked again (`MaxAgain`). */
    private Retry: AppAssistRetry = new AppAssistRetry();
    /**
     * ⭐ At most this many quiet re-asks (2026-09-29): at the service's 15 s that is 5 minutes - the restore after a deploy
     * takes ~3.5 (measured). After it the service's own sentence is shown, as before.
     */
    public static readonly MaxAgain: number = AppAssistRetry.MaxAgain;

    public constructor(Session: AppAssistSession, Seq: number, Host: IAssistHost) {
        this.Session = Session;
        this.Seq = Seq;
        this.Host = Host;
    }

    /**
     * Whether this answer is a refusal that clears on its own and should be asked again quietly - then it is scheduled
     * and the session shows "Getting your archive ready…" (`Waiting`). False = take the answer as it is.
     */
    private Retried = (Answer: AppAssistAnswer): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            if (this.Host.Later === undefined) { return false; }
            if (this.Session.Busy !== true || this.Seq !== this.Session.Seq) { return false; }
            const Wait: number = this.Retry.WaitFor(Answer);
            if (Wait <= 0) { return false; }
            this.Retry.Schedule(Wait, this.Host, this.Again, this.Waiting);
            return true;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistCall.Retried");
            return false;
        }
    };

    // DOCUMENTED EXCEPTION: owner-invoked hook. ERROR-SUPPRESSED-JUSTIFIED:
    // state failures propagate to the original Retried catch; its gate runs before this hook.
    private Waiting = (): void => {
        this.Session.Waiting = true;
        this.Session.Streaming = "";
    };

    /** Ask the same question again - unless it was cancelled or overtaken meanwhile. */
    public Again = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Session.Busy !== true || this.Seq !== this.Session.Seq) { return; }
            if (this.Streamed === true && this.Host.PostStream !== undefined) { this.Host.PostStream(this.Body, this.OnText, this.OnDone); return; }
            this.Host.Post(this.Body, this.OnDone);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistCall.Again");
        }
    };

    /**
     * The answer so far, while it is written. ⛔ TEXT ONLY: re-cut by `AppAssistStream.Visible` here too (the
     * service already did) - no draft, no raw table/chart markup; the draft card comes only with `OnDone`.
     * ⭐ `Rich`: each table/chart slot the service has already cleaned (`AppAssistStream`) - drawn by `Writing`.
     */
    public OnText = (Partial: string, Rich?: AppAssistStreamRich[]): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Session.Busy !== true || this.Seq !== this.Session.Seq) { return; }
            this.Session.Streaming = AppAssistStream.Visible(Partial);
            this.Session.StreamRich = [];
            if (Rich !== undefined) { this.Session.StreamRich = Rich.slice(); }
            this.Host.Answered(this.Session);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistCall.OnText");
        }
    };

    public OnDone = (Status: number, Text: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Answer: AppAssistAnswer = AppAssistApi.Parse(Status, Text, AppAssistApi.LostWhy());
            // ⭐ THE RESTART WINDOW: asked again quietly - the question stays, nothing final is shown (2026-09-29).
            if (this.Retried(Answer) === true) { this.Host.Answered(this.Session); return; }
            if (AppAssistRules.Take(this.Session, this.Seq, Answer, new Date().toISOString()) !== true) { return; }
            if (Answer.Unreadable === true) { this.Session.HistoryState = "unreadable"; }
            this.Host.Answered(this.Session);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistCall.OnDone");
        }
    };
}

/**
 * One draft card's send in flight. It carries its entry and session, so the card shows how it went
 * even if the owner moved to another contact meanwhile (the host repaints whichever panel shows it).
 */
export class AppAssistSend implements IComposerSent {
    private Session: AppAssistSession;
    private Entry: AppAssistEntry;
    private Host: IAssistHost;

    public constructor(Session: AppAssistSession, Entry: AppAssistEntry, Host: IAssistHost) {
        this.Session = Session;
        this.Entry = Entry;
        this.Host = Host;
    }

    /**
     * ⚠ ACCEPTED, NOT DELIVERED: the card says it is in the chat; the bubble there says when it went.
     * ⭐ Noted on its history item as `sent`, so a reload never offers it again.
     */
    public Queued = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.Entry.SendState = AppAssistRules.Sent;
            this.Entry.SendWhy = "";
            this.Entry.Outcome = "sent";
            if (this.Entry.Id.length > 0) { this.Host.Outcome(this.Entry.Id, "sent"); }
            this.Host.Answered(this.Session);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistSend.Queued");
        }
    };

    /** Not queued: the card says why, and Send can be pressed again. */
    public Failed = (Why: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.Entry.SendState = "";
            this.Entry.SendWhy = "Not sent. " + nz(Why);
            this.Host.Answered(this.Session);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistSend.Failed");
        }
    };
}

/**
 * One read of the contact's `ai-chat.json`, answered ONCE, into the session it was asked for - even if the
 * owner moved to another contact meanwhile (the host repaints whichever panel shows it).
 */
export class AppAssistHistoryRead {
    private Session: AppAssistSession;
    private Host: IAssistHost;
    private Ended: boolean = false;
    /**
     * ⭐ The saved ids the log held when this read BEGAN (`AppAssistRules.SavedIds`): an entry among them that the
     * file no longer holds was removed in another tab and leaves this log; one saved after the read began stays.
     */
    private Known: Record<string, boolean>;

    public constructor(Session: AppAssistSession, Host: IAssistHost) {
        this.Session = Session;
        this.Host = Host;
        this.Known = AppAssistRules.SavedIds(Session);
    }

    /** The file's text. ⛔ `AppAiChat.Parse` is strict: an unreadable file shows no history, one sentence. */
    public OnText = (Text: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Ended === true) { return; }
            this.Ended = true;
            let Doc: AppAiChatDoc = new AppAiChatDoc();
            if (nz(Text).trim().length > 0) { Doc = AppAiChat.Parse(Text); }
            AppAssistRules.Adopt(this.Session, Doc, this.Known);
            this.Host.Answered(this.Session);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistHistoryRead.OnText");
        }
    };

    /** No file yet (409 `path/not_found`) - an empty history, not an error. */
    public OnMissing = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Ended === true) { return; }
            this.Ended = true;
            AppAssistRules.Adopt(this.Session, new AppAiChatDoc(), this.Known);
            this.Host.Answered(this.Session);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistHistoryRead.OnMissing");
        }
    };

    /** ⚠ FATE 1: an expired ticket or a dropped connection is a quiet line, never the error handler. */
    public OnFail = (Why: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Ended === true) { return; }
            this.Ended = true;
            this.Session.HistoryState = "failed";
            this.Host.Answered(this.Session);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistHistoryRead.OnFail");
        }
    };
}

export class AppAssistPanel implements IAssistMarks {
    private Doc: IAssistDoc;
    private Log: IAssistNode;
    private Host: IAssistHost;
    private Who: AppAssistWho;
    private Title: string;
    private Tools: IAssistTools;
    /** Every table and chart of the current paint, in drawing order - what "Copy table"/"Save chart" point at. */
    private Exports: AppAssistBlockOut[] = [];
    /** Every chart mark with a `<title>` of the current paint - what `data-wac-mark` points at. */
    private Marks: AppAssistMark[] = [];
    /** The chart being built (its `Exports` index), -1 outside one - the block a mark belongs to. */
    private ChartAt: number = -1;
    /** The conversation this panel shows. Owned by the page, kept per contact for the visit. */
    public Session: AppAssistSession;
    /** A one-off line under the log (validation, "Copied."), gone at the next paint. */
    private Note: string = "";
    /** Characters of rich blocks drawn so far in the answer being drawn (`AppAssistRich.RichMaxChars`). */
    private RichSpent: number = 0;
    /** The entry index and the count of fences so far in the answer being drawn - the id scope. */
    private RichAnswer: number = 0;
    private RichBlocks: number = 0;
    /** The `AppAssistRules.Blocks` index of the block being drawn - what "Keep this" names (`AppPins`). */
    private BlockAt: number = -1;
    /** Drawing a streamed (unfinished) answer: a rich block is its placeholder, never built. */
    private Partial: boolean = false;
    private CitationsAllowed: boolean = true;
    private CitationKeys: string[] = [];
    private LastVisibleRead: number = 0;
    public static readonly VisibleEveryMs: number = 30000;
    /** ⭐ Null = "ask AI about this chat" as ever; set = another question (`IAssistMode`) - and no drafts at all. */
    public Mode: IAssistMode | null = null;
    /**
     * ⭐ "Remove from history" is offered (`AppAssistForget.Offered`). False until the page says this is the owner's
     * own browser - the service refuses everyone else, so nobody else is shown a button that cannot work.
     */
    public CanForget: boolean = false;
    /** The "Remove this answer?" row of the current paint, or null - `RenderInPlace` brings it into view. */
    private ForgetShown: IAssistNode | null = null;
    /**
     * ⭐ THE KEYBOARD FOCUS FOLLOWS "REMOVE FROM HISTORY" (verifier, 2026-10-01). Every paint rebuilds the log, so the
     * button that was pressed is gone and the focus falls to the page's top - a keyboard or screen-reader user then
     * has to find the confirm again. `FocusWhat`: "" (nothing wanted), `cancel` (the confirm's Cancel - ⛔ never its
     * Remove: Enter twice on the bin must not remove), `bin` (the entry's bin: after Cancel or Esc) or `after` (the
     * removal's end: the entry's bin when it was refused, else the bin that took its place). `FocusIds` / `FocusAt`
     * name the entry; `FocusNode` is the control found while painting.
     */
    private FocusWhat: string = "";
    private FocusIds: string[] = [];
    private FocusAt: number = -1;
    private FocusNode: IAssistNode | null = null;
    private FocusOwn: boolean = false;
    /** The clock (ms) the Send guard reads - a field so the suite can move it. */
    public Now: () => number = AppAssistForget.WallNow;

    /**
     * ⛔ `Tools` IS PASSED IN, NEVER IMPORTED: the browser's (`AppAssistTools`) uses DOM globals, and the Bridge
     * compiles this file (`AssistContract.test.ts` -> `AppTaskNotify` -> here) with no DOM lib - "APP RULES LIVE
     * APART FROM APP TRANSPORT". `AppMain` passes `new AppAssistTools()`; null is `AppAssistNoTools` (nothing
     * listened to, a copy refused, a save refused - each said in ink).
     */
    public constructor(Doc: IAssistDoc, Log: IAssistNode, Host: IAssistHost, Session: AppAssistSession,
                       Who: AppAssistWho, Title: string, Tools: IAssistTools | null = null) {
        this.Doc = Doc;
        this.Log = Log;
        this.Host = Host;
        this.Session = Session;
        this.Who = Who;
        this.Title = Title;
        if (Tools === null) { this.Tools = new AppAssistNoTools(); } else { this.Tools = Tools; }
        this.Tools.Listen(Log, this);
    }

    /**
     * Ask `Question`. Answers true when it went (the caller then empties its box); false when a
     * sentence was shown instead, and the box keeps what was typed.
     */
    public Ask = (Question: string, Preset: string = ""): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            // ⭐ `Preset`: a ready-made question the service answers itself (`AppAssistApi.PresetIds`) - by id, never by text.
            let Chosen: string = Preset;
            if (this.Mode !== null) { Chosen = ""; }
            const Why: string = AppAssistRules.Begin(this.Session, Question, Chosen);
            if (Why.length > 0) { this.Say(Why); return false; }
            let Body: string = "";
            if (this.Mode !== null) { Body = this.Mode.Body(this.Session.Asking, AppAssistRules.TurnsOf(this.Session)); }
            if (this.Mode === null) {
                Body = AppAssistApi.Body(this.Who.Phone, this.Who.Key, this.Who.Page, this.Session.Asking,
                    AppAssistRules.TurnsOf(this.Session), this.Session.Preset);
            }
            const Call: AppAssistCall = new AppAssistCall(this.Session, this.Session.Seq, this.Host);
            Call.Body = Body;
            this.Note = "";
            this.Render();
            // ⚠ NO KEY OR NUMBER IS A SIGNED-OUT BROWSER, SAID AS A SENTENCE - not a request the
            // service would only refuse, and not the error handler.
            if (Body.length === 0) { Call.OnDone(200, JSON.stringify({ ok: false, why: AppAssistPanel.SignInWhy() })); return true; }
            // ⭐ STREAMED WHEN THE HOST CAN: the words appear as they are written.
            if (this.Host.PostStream !== undefined) {
                Call.Body = AppAssistStream.Streamed(Body);
                Call.Streamed = true;
                this.Host.PostStream(Call.Body, Call.OnText, Call.OnDone);
                return true;
            }
            this.Host.Post(Body, Call.OnDone);
            return true;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistPanel.Ask");
            return false;
        }
    };

    /**
     * Read the contact's saved history (`ai-chat.json`) and draw it. ⚠ Asked on every contact open, so a
     * question asked on another device shows too; this visit's entries the file now holds show once.
     */
    public LoadHistory = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Reader: AppAssistHistoryRead = new AppAssistHistoryRead(this.Session, this.Host);
            if (this.Session.HistoryState !== "loaded") { this.Session.HistoryState = "loading"; }
            this.Render();
            this.Host.ReadHistory(Reader.OnText, Reader.OnMissing, Reader.OnFail);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistPanel.LoadHistory");
        }
    };

    /** A button in the log was pressed. `Index` is the entry it belongs to. */
    public Act = (Name: string, Index: number): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (Name === "cancel") { AppAssistRules.Cancel(this.Session); this.Note = ""; this.Render(); return; }
            // ⚠ These two carry a BLOCK number (`Exports`), not an entry's - and neither sends anything.
            if (Name === AppAssistExport.CopyAct) { this.CopyTable(Index); return; }
            if (Name === AppAssistExport.SaveAct) { this.SaveChart(Index); return; }
            if (Name === AppAssistKeep.Act) { this.KeepBlock(Index); return; }
            const Entry: AppAssistEntry | null = this.EntryAt(Index);
            if (Entry === null) { return; }
            // ⛔ THE ONE PLACE A DRAFT IS SENT: the owner's click on its card. See the header.
            if (Name === "send") { this.SendDraft(Entry, Index); return; }
            if (Name === "edit") { this.EditDraft(Entry, Index); return; }
            if (Name === "copy" && Entry.Draft.length > 0) { this.Host.Copy(Entry.Draft); return; }
            if (Name === "again") { this.Ask(Entry.Ask, Entry.Preset); return; }
            // ⭐ "Remove from history": the bin asks, Cancel takes the question back, only the confirm's Remove removes.
            if (Name === AppAssistForget.AskAct || Name === AppAssistForget.CancelAct || Name === AppAssistForget.Act) { this.ForgetAct(Name, Entry); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistPanel.Act");
        }
    };

    /**
     * The three clicks of "Remove from history". ⛔ The request goes ONLY from the confirm's Remove (`Act`) on an
     * entry whose confirm is showing - the bin alone removes nothing. ⛔ Nothing here sends a message.
     */
    private ForgetAct = (Name: string, Entry: AppAssistEntry): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (AppAssistForget.Offered(this.CanForget, this.Mode !== null, Entry) !== true) { return; }
            if (Entry.Forget === AppAssistForget.Going) { return; }
            if (Name === AppAssistForget.AskAct) {
                Entry.Forget = AppAssistForget.Asking;
                Entry.ForgetWhy = "";
                this.WantFocus("cancel", Entry);
                this.RenderInPlace();
                return;
            }
            if (Name === AppAssistForget.CancelAct) { Entry.Forget = ""; this.WantFocus("bin", Entry); this.RenderInPlace(); return; }
            if (Entry.Forget !== AppAssistForget.Asking) { return; }
            this.Remove(Entry);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistPanel.ForgetAct");
        }
    };

    /** The confirm's Remove: the one request, the entry faded at once. */
    private Remove = (Entry: AppAssistEntry): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Body: string = AppAssistApi.ForgetBody(this.Who.Phone, this.Who.Key, this.Who.Page, AppAssistForget.IdOf(Entry));
            if (Body.length === 0) {
                Entry.Forget = "";
                Entry.ForgetWhy = AppAssistForget.SignInNote();
                this.WantFocus("bin", Entry);
                this.RenderInPlace();
                return;
            }
            Entry.Forget = AppAssistForget.Going;
            Entry.ForgetWhy = "";
            this.WantFocus("after", Entry);
            this.RenderInPlace();
            const Call: AppAssistForgetCall = new AppAssistForgetCall(this.Session, Entry.Ids, this.Host, Body);
            Call.Now = this.Now;
            this.Host.Post(Body, Call.OnDone);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistPanel.Remove");
        }
    };

    /** Which control takes the focus after the next in-place paint - see `FocusWhat`. */
    private WantFocus = (What: string, Entry: AppAssistEntry): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.FocusWhat = What;
            this.FocusIds = Entry.Ids.slice();
            this.FocusAt = this.Session.Entries.indexOf(Entry);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistPanel.WantFocus");
        }
    };

    /** Whether `One` is the entry the wanted focus names. DOCUMENTED EXCEPTION: pure lookup. ERROR-SUPPRESSED-JUSTIFIED: cannot throw. */
    private FocusIsOn = (One: AppAssistEntry): boolean => {
        for (const Id of One.Ids) { if (this.FocusIds.indexOf(Id) >= 0) { return true; } }
        return false;
    };

    /**
     * A bin was drawn for `One` at `At`: is it the one the focus wants? The entry's own bin (after Cancel, Esc or a
     * refusal); or, once its entry is gone, the bin that took its place - the nearest at or before its index, else
     * the first after it.
     */
    private NoteBin = (One: AppAssistEntry, At: number, Bin: IAssistNode): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.FocusWhat !== "bin" && this.FocusWhat !== "after") { return; }
            if (this.FocusIsOn(One) === true) { this.FocusNode = Bin; this.FocusOwn = true; return; }
            if (this.FocusWhat !== "after" || this.FocusOwn === true) { return; }
            if (this.FocusNode === null || At <= this.FocusAt) { this.FocusNode = Bin; }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistPanel.NoteBin");
        }
    };

    /**
     * After an in-place paint: the wanted control takes the focus, once. ⚠ `after` waits while its entry is still
     * being removed, and then moves the focus ONLY if it is nowhere (the redraw dropped it) - the owner may have
     * gone on to type a question while the service was asked.
     */
    private ApplyFocus = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.FocusWhat.length === 0) { return; }
            if (this.FocusWhat === "after") {
                for (const One of this.Session.Entries) {
                    if (One.Forget === AppAssistForget.Going && this.FocusIsOn(One) === true) { return; }
                }
            }
            const Node: IAssistNode | null = this.FocusNode;
            const Free: boolean = this.FocusWhat !== "after" || this.FocusIsNowhere() === true;
            this.FocusWhat = "";
            this.FocusIds = [];
            this.FocusNode = null;
            if (Node !== null && Free === true && Node.focus !== undefined) { Node.focus(); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistPanel.ApplyFocus");
        }
    };

    /** Whether nothing holds the keyboard focus (a document that cannot say - the suite's - counts as nothing). DOCUMENTED EXCEPTION: pure read. */
    private FocusIsNowhere = (): boolean => {
        const Active: unknown = this.Doc.activeElement;
        return Active === undefined || Active === null || Active === this.Doc.body;
    };

    /**
     * `IAssistMarks.CloseConfirm` - Escape in the log: every open "Remove this answer?" is taken back, exactly as
     * its Cancel does, and the focus returns to that entry's bin. True = there was one. ⛔ Removes nothing, sends nothing.
     */
    public CloseConfirm = (): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            let Open: AppAssistEntry | null = null;
            for (const One of this.Session.Entries) {
                if (One.Forget !== AppAssistForget.Asking) { continue; }
                One.Forget = "";
                Open = One;
            }
            if (Open === null) { return false; }
            this.WantFocus("bin", Open as AppAssistEntry);
            this.RenderInPlace();
            return true;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistPanel.CloseConfirm");
            return false;
        }
    };

    /**
     * `Render`, with the log left where the owner is reading - a confirm that scrolled away from under the click
     * would be a confirm nobody sees.
     */
    public RenderInPlace = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const At: number = this.Log.scrollTop;
            this.ForgetShown = null;
            this.FocusNode = null;
            this.FocusOwn = false;
            this.Render();
            this.Log.scrollTop = At;
            // ⚠ The confirm is taller than the bin it replaced: at the log's lower edge it would be half cut off.
            // ⚠ `Render` set it again; the cast undoes TypeScript's narrowing from the `= null` above.
            const Shown: IAssistNode | null = this.ForgetShown as IAssistNode | null;
            if (Shown !== null && Shown.scrollIntoView !== undefined) { Shown.scrollIntoView({ block: "nearest" }); }
            this.ApplyFocus();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistPanel.RenderInPlace");
        }
    };

    /**
     * The card's Send. ⛔ MARKED `Sending` BEFORE THE HOST IS CALLED, so a second click - even one
     * the browser delivers before the disabled button is painted - finds `CanSend` false.
     * ⚠ The composer box is not touched: the draft goes straight from the card.
     */
    private SendDraft = (Entry: AppAssistEntry, Index: number): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            // ⛔ ONLY THE NEWEST DRAFT, AND ONLY WHILE NOTHING WAS DONE WITH IT - see the header. Never in a mode.
            if (this.Mode !== null || AppAssistRules.CanSendAt(this.Session, Index) !== true) { return; }
            // ⛔ AN ENTRY HAS JUST LEFT THE LOG ("Remove from history"): this click may have been meant for it.
            if (AppAssistForget.SendHeld(this.Session, this.Now()) === true) { return; }
            // ⛔ An entry being removed sends nothing (its confirm is open or its request is in flight).
            if (Entry.Forget.length > 0) { return; }
            Entry.SendState = AppAssistRules.Sending;
            Entry.SendWhy = "";
            this.Note = "";
            this.Render();
            const Why: string = this.Host.Send(Entry.Draft, new AppAssistSend(this.Session, Entry, this.Host));
            if (Why.length > 0) {
                Entry.SendState = "";
                Entry.SendWhy = Why;
            }
            this.Render();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistPanel.SendDraft");
        }
    };

    /**
     * Edit: the draft into the composer, focused - nothing sent. ⭐ Noted as `edited` on its history item, so
     * the card stops offering Send (the owner sends from the box now). ⛔ Only the newest, untouched draft.
     */
    private EditDraft = (Entry: AppAssistEntry, Index: number): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Mode !== null || Entry.Draft.length === 0 || AppAssistRules.LiveAt(this.Session, Index) !== true) { return; }
            if (Entry.SendState.length > 0) { return; }
            // ⛔ An entry being removed is not edited either: its outcome would be recorded on an item that is going.
            if (Entry.Forget.length > 0) { return; }
            this.Host.Fill(Entry.Draft);
            Entry.Outcome = "edited";
            if (Entry.Id.length > 0) { this.Host.Outcome(Entry.Id, "edited"); }
            this.Render();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistPanel.EditDraft");
        }
    };

    private EntryAt = (Index: number): AppAssistEntry | null => {
        if (getCoreUtils().KErrorMode === true) { return null; }
        try {
            if (isNaN(Index) === true || Index < 0 || Index >= this.Session.Entries.length) { return null; }
            return this.Session.Entries[Index];
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistPanel.EntryAt");
            return null;
        }
    };

    /** A one-off line under the log. */
    public Say = (Text: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.Note = nz(Text);
            this.Render();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistPanel.Say");
        }
    };

    /** Draw the whole log again - entries, the question in flight, the note - and scroll to its end. */
    /** Is a streamed answer arriving piece by piece right now? DOCUMENTED EXCEPTION: pure. ERROR-SUPPRESSED-JUSTIFIED: cannot throw. */
    public static Writes = (Busy: boolean, Streaming: string): boolean => {
        return Busy === true && typeof Streaming === "string" && Streaming.length > 0;
    };

    public Render = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            // ⭐ WHILE AN ANSWER IS BEING WRITTEN THE LOG SAYS IT IS BUSY (audit 2026-10-12). The log is a polite live
            // region and is redrawn for every streamed piece: a screen reader was handed the answer 18 times over,
            // in fragments. `aria-busy` holds the reading until the last paint, which says it is done - set BEFORE
            // anything is written, so no piece is ever announced on its own.
            this.Log.setAttribute("aria-busy", String(AppAssistPanel.Writes(this.Session.Busy, this.Session.Streaming)));
            this.Log.textContent = "";
            this.Exports = [];
            this.Marks = [];
            this.ChartAt = -1;
            if (this.Session.Entries.length === 0 && this.Session.Busy !== true) {
                // ⛔ No helper text in an empty log (owner 2026-11-02: "Take away helper texts"); a mode may still have one.
                let Intro: string = "";
                if (this.Mode !== null) { Intro = this.Mode.Intro(); }
                // ⚠ A MODE WHOSE EXPLANATION IS A TOOLTIP ("") DRAWS NOTHING, so its empty log can collapse.
                if (Intro.length > 0) { this.Log.appendChild(this.Text("div", "aintro", Intro)); }
            }
            this.HistoryLine();
            const Now: Date = new Date();
            let Day: string = "";
            for (let At: number = 0; At < this.Session.Entries.length; At = At + 1) {
                Day = this.DayLine(this.Session.Entries[At].At, Day, Now);
                this.Entry(this.Session.Entries[At], At);
            }
            if (this.Session.Busy === true) { this.DayLine("", Day, Now); this.Thinking(); }
            if (this.Note.length > 0) { this.Log.appendChild(this.Text("div", "anote", this.Note)); }
            this.Log.scrollTop = this.Log.scrollHeight;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistPanel.Render");
        }
    };

    /** Where the saved history stands, when it is not simply there: loading, unreadable, not reachable. */
    private HistoryLine = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const State: string = this.Session.HistoryState;
            if (State === "loading") {
                const Row: IAssistNode = this.Doc.createElement("div");
                Row.className = "ahist";
                Row.setAttribute("role", "status");
                const Dots: IAssistNode = this.Text("span", "adots", "");
                Dots.setAttribute("aria-hidden", "true");
                Row.appendChild(Dots);
                Row.appendChild(this.Text("span", "", AppAssistPanel.LoadingNote()));
                this.Log.appendChild(Row);
            }
            if (State === "unreadable") { this.Log.appendChild(this.Text("div", "ahist", AppAssistPanel.UnreadableNote())); }
            if (State === "failed") { this.Log.appendChild(this.Text("div", "ahist", AppAssistPanel.FailedNote())); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistPanel.HistoryLine");
        }
    };

    /** A day line before the first entry of each day (the browser's day). Answers the day drawn. */
    private DayLine = (Iso: string, Last: string, Now: Date): string => {
        if (getCoreUtils().KErrorMode === true) { return Last; }
        try {
            const Key: string = AppAssistRules.DayKey(Iso, Now);
            if (Key.length === 0 || Key === Last) { return Last; }
            const Line: IAssistNode = this.Text("div", "aday", AppAssistRules.DayLabel(Iso, Now));
            Line.setAttribute("role", "separator");
            this.Log.appendChild(Line);
            return Key;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistPanel.DayLine");
            return Last;
        }
    };

    private Entry = (One: AppAssistEntry, At: number): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            // ⭐ A CLOSED TASK'S RECORD reads as the task done, not as a question the owner typed.
            if (One.TaskId.length > 0 && One.Ask.length > 0) { this.Put(One, this.Text("div", "aq atask", "Done: " + One.Ask)); }
            if (One.TaskId.length === 0 && One.Ask.length > 0) { this.Put(One, this.Text("div", "aq", One.Ask)); }
            if (One.Ok !== true) {
                // ⭐ A TIMED-OUT OR FAILED QUESTION KEEPS THE WORDS THAT HAD STREAMED - marked as a partial answer.
                if (One.Partial.length > 0) {
                    const Kept: IAssistNode = this.Answer(One.Partial, At, false);
                    Kept.className = "aa apartial";
                    this.Log.appendChild(Kept);
                }
                const Why: IAssistNode = this.Text("div", "awhy", One.Why);
                Why.appendChild(this.Button("again", At, "abtn btn secondary sm", "Try again"));
                this.Log.appendChild(Why);
                return;
            }
            if (One.Answer.trim().length > 0) {
                let Keys: string[] = [];
                if (One.SourceTrust === AppAssistCitations.Trust) { Keys = One.SourceKeys; }
                this.Put(One, this.Answer(One.Answer, At, true, Keys));
            }
            // ⛔ A MODE PANEL NEVER DRAWS A DRAFT - no card, no Send, no Edit (see `IAssistMode`).
            if (One.Draft.length > 0 && this.Mode === null) { this.Put(One, this.Draft(One, At)); }
            this.ForgetRow(One, At);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistPanel.Entry");
        }
    };

    /** One piece of an entry into the log - faded (`agoing`) while that entry's removal is in flight. */
    private Put = (One: AppAssistEntry, Node: IAssistNode): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (One.Forget === AppAssistForget.Going) { Node.className = (Node.className + " " + AppAssistPanel.GoingClass).trim(); }
            this.Log.appendChild(Node);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistPanel.Put");
        }
    };

    /**
     * ⭐ "Remove from history" under a SAVED entry (`AppAssistForget.Offered`): the bin; or, once pressed, "Remove this
     * answer?" with Remove and Cancel; or "Removing…" with the moving dots while the service is asked. A refusal's
     * sentence stands beside the bin, in ink. ⛔ No button here sends anything.
     */
    private ForgetRow = (One: AppAssistEntry, At: number): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (AppAssistForget.Offered(this.CanForget, this.Mode !== null, One) !== true) { return; }
            const Row: IAssistNode = this.Doc.createElement("div");
            Row.className = "aforget";
            if (One.Forget === AppAssistForget.Going) { this.ForgetGoing(Row); this.Log.appendChild(Row); return; }
            if (One.Forget === AppAssistForget.Asking) {
                const Cancel: IAssistNode = this.ForgetConfirm(Row, At, AppAssistForget.IdOf(One));
                if (this.FocusWhat === "cancel" && this.FocusIsOn(One) === true) { this.FocusNode = Cancel; }
                this.Log.appendChild(Row);
                return;
            }
            const Bin: IAssistNode = this.Button(AppAssistForget.AskAct, At, "aforgetbtn btn ghost icon sm", "");
            Bin.setAttribute(AppAssistForget.ItemAttr, AppAssistForget.IdOf(One));
            Bin.setAttribute("aria-label", AppAssistForget.Label());
            Bin.setAttribute("title", AppAssistForget.Label());
            const Icon: IIconNode | null = AppIcons.Build(this.Doc as unknown as IIconDoc, AppIcons.Trash, 16);
            if (Icon !== null) { Bin.appendChild(Icon as unknown as IAssistNode); }
            Row.appendChild(Bin);
            this.NoteBin(One, At, Bin);
            if (One.ForgetWhy.length > 0) {
                const Why: IAssistNode = this.Text("span", "aforgetwhy", One.ForgetWhy);
                Why.setAttribute("role", "status");
                Row.appendChild(Why);
            }
            this.Log.appendChild(Row);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistPanel.ForgetRow");
        }
    };

    /** The inline confirm: the question, then Remove (the one button that removes) and Cancel. Answers its Cancel button. */
    private ForgetConfirm = (Row: IAssistNode, At: number, Item: string): IAssistNode => {
        if (getCoreUtils().KErrorMode === true) { return Row; }
        try {
            Row.className = "aforget aforgetask";
            this.ForgetShown = Row;
            Row.setAttribute("role", "group");
            Row.setAttribute("aria-label", AppAssistForget.Question());
            Row.appendChild(this.Text("span", "aforgetq", AppAssistForget.Question()));
            const Yes: IAssistNode = this.Button(AppAssistForget.Act, At, "abtn btn danger sm", AppAssistForget.YesLabel());
            Yes.setAttribute(AppAssistForget.ItemAttr, Item);
            Row.appendChild(Yes);
            const No: IAssistNode = this.Button(AppAssistForget.CancelAct, At, "abtn btn secondary sm", AppAssistForget.NoLabel());
            Row.appendChild(No);
            return No;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistPanel.ForgetConfirm");
            return Row;
        }
    };

    /** The removal in flight: moving dots and "Removing…" - the entry above it is faded. */
    private ForgetGoing = (Row: IAssistNode): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            Row.setAttribute("role", "status");
            const Dots: IAssistNode = this.Text("span", "adots", "");
            Dots.setAttribute("aria-hidden", "true");
            Row.appendChild(Dots);
            Row.appendChild(this.Text("span", "aforgetq", AppAssistForget.GoingNote()));
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistPanel.ForgetGoing");
        }
    };

    /**
     * The answer's words with their simple Markdown as elements (`AppAssistRules.Blocks`).
     * ⛔ Every word goes in through `Text` - `textContent` - so nothing the model writes is markup.
     */
    private Answer = (Words: string, At: number, Verified: boolean = true, Keys: string[] = []): IAssistNode => {
        if (getCoreUtils().KErrorMode === true) { return this.Doc.createElement("div"); }
        try {
            const Box: IAssistNode = this.Doc.createElement("div");
            Box.className = "aa";
            this.RichSpent = 0;
            this.RichAnswer = At;
            this.RichBlocks = 0;
            this.CitationsAllowed = Verified;
            this.CitationKeys = Keys;
            const Blocks: AppAssistBlock[] = AppAssistRules.Blocks(Words);
            for (let N: number = 0; N < Blocks.length; N = N + 1) {
                this.BlockAt = N;
                Box.appendChild(this.BlockNode(Blocks[N]));
            }
            this.BlockAt = -1;
            this.CitationsAllowed = true;
            this.CitationKeys = [];
            return Box;
        } catch (e) {
            this.CitationsAllowed = true;
            this.CitationKeys = [];
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistPanel.Answer");
            return this.Doc.createElement("div");
        }
    };

    /** One block: a paragraph or list, a pipe table, or a cleaned ```wac-html fence. */
    private BlockNode = (Block: AppAssistBlock): IAssistNode => {
        if (getCoreUtils().KErrorMode === true) { return this.Doc.createElement("div"); }
        try {
            if (Block.Kind === "rich" && this.Partial === true) { return this.Text("div", "amuted", AppAssistStream.Placeholder); }
            if (Block.Kind === "rich") { return this.Rich(Block.Source); }
            if (Block.Kind === "table") { return this.Table(Block); }
            const Outer: IAssistNode = this.Doc.createElement(Block.Kind);
            if (Block.Kind === "ol" && Block.Start !== 1) { Outer.setAttribute("start", String(Block.Start)); }
            this.Lines(Outer, Block);
            return Outer;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistPanel.BlockNode");
            return this.Doc.createElement("div");
        }
    };

    /** A Markdown pipe table in its own scroller (`.atable`), every cell's words as text runs. */
    private Table = (Block: AppAssistBlock): IAssistNode => {
        if (getCoreUtils().KErrorMode === true) { return this.Doc.createElement("div"); }
        try {
            const Table: IAssistNode = this.Doc.createElement("table");
            const Head: IAssistNode = this.Doc.createElement("thead");
            Head.appendChild(this.TableRow(Block.Head, Block.Align, "th"));
            Table.appendChild(Head);
            const Body: IAssistNode = this.Doc.createElement("tbody");
            for (const Row of Block.Rows) { Body.appendChild(this.TableRow(Row, Block.Align, "td")); }
            Table.appendChild(Body);
            // ⚠ A pipe table has no caption, so a screen reader is told it is a table.
            Table.setAttribute("aria-label", AppAssistExport.TableName);
            return this.TableOut(this.Wrap("atable", Table), AppAssistExport.RowsOfBlock(Block));
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistPanel.Table");
            return this.Doc.createElement("div");
        }
    };

    private TableRow = (Cells: AppAssistSpan[][], Align: string[], Tag: string): IAssistNode => {
        if (getCoreUtils().KErrorMode === true) { return this.Doc.createElement("tr"); }
        try {
            const Row: IAssistNode = this.Doc.createElement("tr");
            for (let Col: number = 0; Col < Cells.length; Col = Col + 1) {
                const Cell: IAssistNode = this.Doc.createElement(Tag);
                if (Col < Align.length && Align[Col].length > 0) { Cell.setAttribute("data-align", Align[Col]); }
                for (const Span of Cells[Col]) { Cell.appendChild(this.Run(Span)); }
                Row.appendChild(Cell);
            }
            return Row;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistPanel.TableRow");
            return this.Doc.createElement("tr");
        }
    };

    /**
     * A ```wac-html fence: ⛔ CLEANED BY THE SHARED ALLOW-LIST FIRST (`kHtmlSafeCore.Clean`), then
     * BUILT node by node. Over the answer's budget, over a limit, or nothing left: a muted note.
     * ⚠ The note is shown content (the model drew something we will not draw), never the handler.
     */
    private Rich = (Source: string): IAssistNode => {
        if (getCoreUtils().KErrorMode === true) { return this.Doc.createElement("div"); }
        try {
            if (AppAssistRich.Fits(this.RichSpent, Source.length) !== true) { return this.Text("div", "amuted", AppAssistRich.TooLargeNote); }
            this.RichSpent = this.RichSpent + Source.length;
            this.RichBlocks = this.RichBlocks + 1;
            // ⚠ Its own id prefix per block (`AppAssistRich.Policy`): the log is one document.
            const Scope: string = String(this.RichAnswer) + "-" + String(this.RichBlocks) + "-";
            const Result: kHtmlSafeResult = kHtmlSafeCore.Clean(Source, AppAssistRich.Policy(Scope));
            let Survived: boolean = false;
            if (Result.Root !== null && Result.Root.childNodes.length > 0) { Survived = true; }
            const Note: string = AppAssistRich.NoteFor(Result.Refused, Survived);
            // ⚠ `Survived` false makes `Note` non-empty; the null test is for the compiler.
            if (Note.length > 0 || Result.Root === null) { return this.Text("div", "amuted", Note); }
            const Box: IAssistNode = this.Doc.createElement("div");
            Box.className = "arich";
            for (const Kid of Result.Root.childNodes) { this.Kid(Box, Kid); }
            return Box;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistPanel.Rich");
            return this.Doc.createElement("div");
        }
    };

    /** One cleaned node into `Into`: text as a text node, an element built; anything else dropped. */
    private Kid = (Into: IAssistNode, Node: IkDomNode): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (Node.nodeType === 3) { Into.appendChild(this.Doc.createTextNode(nz(Node.nodeValue))); return; }
            if (Node.nodeType === 1) { Into.appendChild(this.Build(Node as IkDomElement)); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistPanel.Kid");
        }
    };

    /**
     * One cleaned element, rebuilt: `createElementNS` inside a chart, `createElement` outside; each
     * attribute the CLEANED element still carries, one by one, under its written name (SVG's
     * `viewBox` is case-sensitive). ⛔ No markup is ever parsed by the browser here.
     * A table goes in `.atable`, an outermost `<svg>` in `.achart` - each with its tools row (`TableOut`,
     * `ChartOut`). ⚠ An outermost chart takes its `Exports` number BEFORE its children are built, so a
     * mark inside it knows which chart's value line is its own.
     */
    private Build = (El: IkDomElement): IAssistNode => {
        if (getCoreUtils().KErrorMode === true) { return this.Doc.createElement("span"); }
        try {
            const Tag: string = kHtmlSafeCore.TagOf(El);
            const Svg: boolean = AppAssistPanel.IsSvg(El);
            const Parent: IkDomElement | null = El.parentNode;
            const Outer: boolean = Tag === "svg" && (Parent === null || AppAssistPanel.IsSvg(Parent) !== true);
            let Node: IAssistNode;
            if (Svg === true) { Node = this.Doc.createElementNS(kHtmlSafeCore.SvgNs, Tag); } else { Node = this.Doc.createElement(Tag); }
            for (const Name of kHtmlSafeCore.AttributeNamesOf(El)) {
                // ⚠ `xmlns` is what `createElementNS` already did; as an attribute it only adds noise.
                const Value: string | null = kHtmlParserCore.AttributeOf(El, Name);
                if (Value !== null && Name !== "xmlns") { Node.setAttribute(Name, Value); }
            }
            const Around: number = this.ChartAt;
            if (Outer === true) { this.ChartAt = this.NewOut("chart"); }
            this.Name(El, Node, Tag, Svg, Outer);
            for (const Kid of El.childNodes) { this.Kid(Node, Kid); }
            if (Svg !== true && Tag === "table") { return this.TableOut(this.Wrap("atable", Node), AppAssistExport.RowsOfTable(El)); }
            if (Outer === true) {
                const Chart: number = this.ChartAt;
                this.ChartAt = Around;
                return this.ChartOut(Chart, Node);
            }
            return Node;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistPanel.Build");
            return this.Doc.createElement("span");
        }
    };

    /**
     * What a screen reader and a tap need on a built element: a chart the model did not name gets
     * `role="img"` and its own `<title>` (or "Chart") as its label; a table with no `<caption>` is "Table";
     * a mark inside a chart that has its own `<title>` is focusable and numbered (`Marks`).
     * ⚠ Never over what the model wrote: an `aria-label` or `role` it gave stays.
     */
    private Name = (El: IkDomElement, Node: IAssistNode, Tag: string, Svg: boolean, Outer: boolean): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (Outer === true && Node.getAttribute("aria-label") === null) { Node.setAttribute("aria-label", AppAssistExport.ChartLabel(El)); }
            if (Outer === true && Node.getAttribute("role") === null) { Node.setAttribute("role", "img"); }
            if (Svg !== true && Tag === "table" && AppAssistExport.HasCaption(El) !== true) {
                Node.setAttribute("aria-label", AppAssistExport.TableName);
            }
            if (Svg !== true || this.ChartAt < 0 || AppAssistExport.IsMark(El, Outer) !== true) { return; }
            const Mark: AppAssistMark = new AppAssistMark();
            Mark.Block = this.ChartAt;
            Mark.Text = AppAssistExport.TitleOf(El);
            Mark.Node = Node;
            Node.setAttribute("tabindex", "0");
            Node.setAttribute(AppAssistExport.MarkAttr, String(this.Marks.length));
            this.Marks.push(Mark);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistPanel.Name");
        }
    };

    /** A new entry in `Exports` of this kind; answers its index. A chart's `Seq` counts the log's charts from 1. */
    private NewOut = (Kind: string): number => {
        if (getCoreUtils().KErrorMode === true) { return -1; }
        try {
            const Out: AppAssistBlockOut = new AppAssistBlockOut();
            Out.Kind = Kind;
            let Seq: number = 1;
            for (const One of this.Exports) { if (One.Kind === Kind) { Seq = Seq + 1; } }
            Out.Seq = Seq;
            Out.Block = this.BlockAt;
            Out.Item = this.KeepItem();
            this.Exports.push(Out);
            return this.Exports.length - 1;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistPanel.NewOut");
            return -1;
        }
    };

    /** A table's scroller inside its block, with its "Copy table" row. */
    private TableOut = (Scroller: IAssistNode, Rows: string[][]): IAssistNode => {
        if (getCoreUtils().KErrorMode === true) { return Scroller; }
        try {
            const At: number = this.NewOut("table");
            if (At < 0) { return Scroller; }
            this.Exports[At].Rows = Rows;
            const Box: IAssistNode = this.Wrap("ablock", Scroller);
            Box.appendChild(this.ToolRow(At, AppAssistExport.CopyAct, AppAssistExport.CopyLabel(), AppAssistExport.CopyAria()));
            return Box;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistPanel.TableOut");
            return Scroller;
        }
    };

    /** A chart's frame inside its block: the value line a tapped mark fills, and the "Save chart" row. */
    private ChartOut = (At: number, Svg: IAssistNode): IAssistNode => {
        if (getCoreUtils().KErrorMode === true) { return Svg; }
        try {
            const Frame: IAssistNode = this.Wrap("achart", Svg);
            if (At < 0 || At >= this.Exports.length) { return Frame; }
            const Box: IAssistNode = this.Wrap("ablock", Frame);
            const Value: IAssistNode = this.Text("div", "avalue", "");
            Box.appendChild(Value);
            this.Exports[At].Chart = Svg;
            this.Exports[At].Value = Value;
            Box.appendChild(this.ToolRow(At, AppAssistExport.SaveAct, AppAssistExport.SaveLabel(), AppAssistExport.SaveAria()));
            return Box;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistPanel.ChartOut");
            return Svg;
        }
    };

    /** The row under a block: its note line (changed in place) and its one small button. */
    private ToolRow = (At: number, Act: string, Label: string, Aria: string): IAssistNode => {
        if (getCoreUtils().KErrorMode === true) { return this.Doc.createElement("div"); }
        try {
            const Row: IAssistNode = this.Doc.createElement("div");
            Row.className = "atools";
            const Note: IAssistNode = this.Text("span", "anote", "");
            Note.setAttribute("role", "status");
            Row.appendChild(Note);
            this.Exports[At].Note = Note;
            const Button: IAssistNode = this.Button(Act, At, "atool btn ghost sm", Label);
            Button.setAttribute("aria-label", Aria);
            Button.setAttribute("title", Aria);
            Row.appendChild(Button);
            if (this.Exports[At].Item.length > 0) { Row.appendChild(this.KeepButton(At)); }
            return Row;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistPanel.ToolRow");
            return this.Doc.createElement("div");
        }
    };

    /**
     * The saved item a block of the answer being drawn can be kept from - its entry's last history id - or "" when it
     * cannot: a streamed (unfinished) answer, another question mode, or an answer the history did not save.
     */
    private KeepItem = (): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (this.Partial === true || this.Mode !== null || this.BlockAt < 0) { return ""; }
            if (this.RichAnswer < 0 || this.RichAnswer >= this.Session.Entries.length) { return ""; }
            const Entry: AppAssistEntry = this.Session.Entries[this.RichAnswer];
            if (Entry.TaskId.length > 0) { return ""; }
            return AppPins.ItemOf(Entry.Ids);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistPanel.KeepItem");
            return "";
        }
    };

    /** "Keep this" - or "Kept", disabled, when this block was kept on this visit. */
    private KeepButton = (At: number): IAssistNode => {
        if (getCoreUtils().KErrorMode === true) { return this.Doc.createElement("button"); }
        try {
            const Out: AppAssistBlockOut = this.Exports[At];
            const Kept: boolean = AppAssistKeep.Kept[AppAssistKeep.KeyOf(this.Who.Page, Out.Item, Out.Block)] === true;
            let Label: string = AppAssistKeep.Label();
            if (Kept === true) { Label = AppAssistKeep.KeptLabel(); }
            const Button: IAssistNode = this.Button(AppAssistKeep.Act, At, "atool akeep btn ghost sm", Label);
            Button.setAttribute("aria-label", AppAssistKeep.Aria());
            Button.setAttribute("title", AppAssistKeep.Aria());
            if (Kept === true) { Button.setAttribute("disabled", ""); }
            Out.Keep = Button;
            return Button;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistPanel.KeepButton");
            return this.Doc.createElement("button");
        }
    };

    /**
     * "Keep this": block `At` into the contact's Insights (`/assist` `pin`, `AppAssistKeep`). ⛔ The body carries the
     * item id and the block number only - never markup. One request per block at a time; nothing is sent to anyone.
     */
    private KeepBlock = (At: number): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (isNaN(At) === true || At < 0 || At >= this.Exports.length || this.Exports[At].Note === null) { return; }
            const Out: AppAssistBlockOut = this.Exports[At];
            const Note: IAssistNode = Out.Note as IAssistNode;
            if (Out.Item.length === 0) { Note.textContent = AppAssistKeep.NotSavedNote(); return; }
            const Key: string = AppAssistKeep.KeyOf(this.Who.Page, Out.Item, Out.Block);
            if (AppAssistKeep.Kept[Key] === true || AppAssistKeep.Busy[Key] === true) { return; }
            const Body: string = AppAssistApi.PinBody(this.Who.Phone, this.Who.Key, this.Who.Page, Out.Item, Out.Block);
            if (Body.length === 0) { Note.textContent = AppAssistKeep.SignInNote(); return; }
            AppAssistKeep.Busy[Key] = true;
            Note.textContent = AppAssistKeep.KeepingNote();
            this.Host.Post(Body, new AppAssistKeepCall(Key, Note, Out.Keep, this.Host, Body).OnDone);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistPanel.KeepBlock");
        }
    };

    /** The block `At` if it is of `Kind`, else null (a stale button from an earlier paint points at nothing). */
    private OutAt = (At: number, Kind: string): AppAssistBlockOut | null => {
        if (getCoreUtils().KErrorMode === true) { return null; }
        try {
            if (isNaN(At) === true || At < 0 || At >= this.Exports.length) { return null; }
            if (this.Exports[At].Kind !== Kind || this.Exports[At].Note === null) { return null; }
            return this.Exports[At];
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistPanel.OutAt");
            return null;
        }
    };

    /** "Copy table": its rows as TSV, header first, to the clipboard; the answer on the table's own note line. */
    private CopyTable = (At: number): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Out: AppAssistBlockOut | null = this.OutAt(At, "table");
            if (Out === null) { return; }
            const Note: IAssistNode = Out.Note as IAssistNode;
            Note.textContent = "";
            this.Tools.Copy(AppAssistExport.TsvOf(Out.Rows), new AppAssistTableCopy(Note));
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistPanel.CopyTable");
        }
    };

    /**
     * "Save chart": the BUILT chart, as "<contact>-chart-<n>.svg". Any shown value is hidden first, so the
     * file does not carry a highlight. The note says the name, or why the browser could not.
     */
    private SaveChart = (At: number): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Out: AppAssistBlockOut | null = this.OutAt(At, "chart");
            if (Out === null || Out.Chart === null) { return; }
            this.HideMarks();
            const Name: string = AppAssistExport.FileName(this.Title, AppAssistRules.IsRawId(this.Title), Out.Seq);
            const Why: string = this.Tools.Save(Out.Chart, Name);
            let Said: string = AppAssistExport.SavedNote(Name);
            if (Why.length > 0) { Said = Why; }
            (Out.Note as IAssistNode).textContent = Said;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistPanel.SaveChart");
        }
    };

    /** `IAssistMarks.ShowMark`: mark `N`'s title on its chart's value line; every other line emptied. */
    public ShowMark = (N: number): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (isNaN(N) === true || N < 0 || N >= this.Marks.length) { this.HideMarks(); return; }
            const Mark: AppAssistMark = this.Marks[N];
            this.HideMarks();
            if (Mark.Block < 0 || Mark.Block >= this.Exports.length) { return; }
            const Value: IAssistNode | null = this.Exports[Mark.Block].Value;
            if (Value !== null) { Value.textContent = Mark.Text; }
            if (Mark.Node !== null) { Mark.Node.setAttribute(AppAssistExport.OnAttr, "1"); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistPanel.ShowMark");
        }
    };

    /** `IAssistMarks.HideMarks`: every chart's value line empty, no mark highlighted. */
    public HideMarks = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            for (const Out of this.Exports) { if (Out.Value !== null && nz(Out.Value.textContent).length > 0) { Out.Value.textContent = ""; } }
            for (const Mark of this.Marks) {
                if (Mark.Node !== null && Mark.Node.getAttribute(AppAssistExport.OnAttr) === "1") { Mark.Node.setAttribute(AppAssistExport.OnAttr, "0"); }
            }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistPanel.HideMarks");
        }
    };

    /** `Inner` inside a `div` of class `Cls` - the table's own scroller or the chart's frame. */
    private Wrap = (Cls: string, Inner: IAssistNode): IAssistNode => {
        if (getCoreUtils().KErrorMode === true) { return Inner; }
        try {
            const Box: IAssistNode = this.Doc.createElement("div");
            Box.className = Cls;
            Box.appendChild(Inner);
            return Box;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistPanel.Wrap");
            return Inner;
        }
    };

    /** Whether a cleaned element is SVG: the `<svg>` itself or anything inside one. */
    private static IsSvg = (El: IkDomElement): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            return kHtmlSafeCore.TagOf(El) === "svg" || kHtmlSafeCore.InSvg(El) === true;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistPanel.IsSvg");
            return false;
        }
    };

    /** A paragraph's lines, joined by `<br>`; or a list's items, one `<li>` each. */
    private Lines = (Outer: IAssistNode, Block: AppAssistBlock): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            for (let At: number = 0; At < Block.Lines.length; At = At + 1) {
                let Into: IAssistNode = Outer;
                if (Block.Kind !== "p") { Into = this.Doc.createElement("li"); Outer.appendChild(Into); }
                if (Block.Kind === "p" && At > 0) { Outer.appendChild(this.Doc.createElement("br")); }
                for (const Span of Block.Lines[At]) { Into.appendChild(this.Run(Span)); }
            }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistPanel.Lines");
        }
    };

    /** One run: `<code>`, `<strong>`, `<em>` (bold AND italic nest), or a plain `<span>` - all as TEXT. */
    private Run = (Span: AppAssistSpan): IAssistNode => {
        if (getCoreUtils().KErrorMode === true) { return this.Doc.createElement("span"); }
        try {
            if (Span.Code === true) { return this.Text("code", "", Span.Text); }
            if (Span.Bold === true && Span.Italic === true) {
                const Strong: IAssistNode = this.Doc.createElement("strong");
                Strong.appendChild(this.Words("em", Span.Text));
                return Strong;
            }
            if (Span.Bold === true) { return this.Words("strong", Span.Text); }
            if (Span.Italic === true) { return this.Words("em", Span.Text); }
            return this.Words("span", Span.Text);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistPanel.Run");
            return this.Doc.createElement("span");
        }
    };

    /**
     * A run's words in a `Tag`: as TEXT with no mode (exactly `Text`); with a mode, its pieces - text nodes and
     * `<a class="alink">` links to the owner's own chats (`AppAssistPiece`). ⛔ Words are always text nodes; a
     * link's address is built from the piece's index href, never from anything the model wrote.
     */
    private Words = (Tag: string, Words: string): IAssistNode => {
        if (getCoreUtils().KErrorMode === true) { return this.Doc.createElement(Tag); }
        try {
            const El: IAssistNode = this.Doc.createElement(Tag);
            const Parts: AppCitationPart[] = AppAssistCitations.Parts(Words, this.CitationKeys);
            if (this.Mode === null && (Parts.length === 1 || this.Partial === true || this.CitationsAllowed === false)) {
                return this.Text(Tag, "", Words);
            }
            if (this.Partial === true || this.CitationsAllowed === false) { this.WordPieces(El, Words); return El; }
            for (const Part of Parts) {
                if (Part.ChatId.length === 0) { this.WordPieces(El, Part.Text); continue; }
                const Button: IAssistNode = this.Text("button", "alink evidence-link btn secondary sm", "Source");
                Button.setAttribute("type", "button");
                Button.setAttribute("data-search-chat", Part.ChatId);
                Button.setAttribute("data-search-message", Part.MessageId);
                Button.setAttribute("aria-label", "View the source message and surrounding conversation");
                El.appendChild(Button);
            }
            return El;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistPanel.Words");
            return this.Doc.createElement(Tag);
        }
    };

    private WordPieces = (El: IAssistNode, Words: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Mode === null) { El.appendChild(this.Doc.createTextNode(Words)); return; }
            for (const Piece of this.Mode.Pieces(Words)) {
                if (Piece.Href.length === 0) { El.appendChild(this.Doc.createTextNode(Piece.Text)); continue; }
                const Link: IAssistNode = this.Text("a", "alink", Piece.Text);
                Link.setAttribute("href", "#c/" + encodeURIComponent(Piece.Href));
                Link.setAttribute("aria-label", "Open the chat with " + Piece.Label);
                Link.setAttribute("title", "Open the chat with " + Piece.Label);
                El.appendChild(Link);
            }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistPanel.WordPieces");
        }
    };

    /**
     * The draft as the message it would be - a bubble. ⛔ Send and Edit only on the newest draft while nothing
     * was done with it (or its send on this visit is in flight / accepted); every other draft offers Copy
     * and says what became of it.
     */
    private Draft = (One: AppAssistEntry, At: number): IAssistNode => {
        if (getCoreUtils().KErrorMode === true) { return this.Doc.createElement("div"); }
        try {
            const Box: IAssistNode = this.Doc.createElement("div");
            Box.className = "adraft";
            Box.appendChild(this.Text("div", "adlabel", "Suggested message"));
            Box.appendChild(this.Text("div", "adbubble", One.Draft));
            const Acts: IAssistNode = this.Doc.createElement("div");
            Acts.className = "aacts";
            Acts.appendChild(this.Button("copy", At, "abtn btn secondary sm", "Copy"));
            const Live: boolean = AppAssistRules.LiveAt(this.Session, At) === true || One.SendState.length > 0;
            if (Live === true) {
                const Edit: IAssistNode = this.Button("edit", At, "abtn btn secondary sm", "Edit");
                if (One.Forget.length > 0) { Edit.setAttribute("disabled", ""); }
                Acts.appendChild(Edit);
                Acts.appendChild(this.SendButton(One, At));
            }
            Box.appendChild(Acts);
            if (One.SendWhy.length > 0) { Box.appendChild(this.Text("div", "adwhy", One.SendWhy)); }
            if (One.SendState === AppAssistRules.Sent) { Box.appendChild(this.Text("div", "adsent", AppAssistPanel.SentNote())); }
            if (Live === false && One.Outcome.length > 0) { Box.appendChild(this.Text("div", "adout", AppAssistRules.OutcomeLabel(One.Outcome))); }
            return Box;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistPanel.Draft");
            return this.Doc.createElement("div");
        }
    };

    /** "Send to Anna"; "Sending…" and "In the chat ✓" disabled; disabled too when it cannot go. */
    private SendButton = (One: AppAssistEntry, At: number): IAssistNode => {
        if (getCoreUtils().KErrorMode === true) { return this.Doc.createElement("button"); }
        try {
            let Label: string = AppAssistRules.SendLabel(this.Title);
            if (One.SendState === AppAssistRules.Sending) { Label = "Sending…"; }
            if (One.SendState === AppAssistRules.Sent) { Label = "In the chat"; }
            const El: IAssistNode = this.Button("send", At, "abtn ause btn send sm", Label);
            // ⭐ The "it went in" mark is our tick icon, not a typed ✓ (design system, 2026-09-30).
            if (One.SendState === AppAssistRules.Sent) {
                const Tick: IIconNode | null = AppIcons.Build(this.Doc as unknown as IIconDoc, AppIcons.Check, 16);
                if (Tick !== null) { El.appendChild(Tick as unknown as IAssistNode); }
            }
            if (AppAssistRules.CanSendAt(this.Session, At) !== true) { El.setAttribute("disabled", ""); }
            // ⚠ `SendDraft` refuses while this entry's removal is being confirmed or is in flight - so the button SAYS so
            // (a live-looking Send that does nothing is a broken button).
            if (One.Forget.length > 0) { El.setAttribute("disabled", ""); }
            return El;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistPanel.SendButton");
            return this.Doc.createElement("button");
        }
    };

    private Thinking = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.Log.appendChild(this.Text("div", "aq", this.Session.Asking));
            if (this.Session.Streaming.length > 0) { this.Writing(); return; }
            const Row: IAssistNode = this.Doc.createElement("div");
            Row.className = "athink";
            Row.setAttribute("role", "status");
            const Dots: IAssistNode = this.Text("span", "adots", "");
            Dots.setAttribute("aria-hidden", "true");
            Row.appendChild(Dots);
            // ⭐ Right after a deploy the service is still bringing the archive back: said quietly, never as a failure.
            if (this.Session.Waiting === true) { Row.appendChild(this.Text("span", "athinkline", AppAssistPanel.ReadyingStatus())); }
            if (this.Session.Waiting !== true) { Row.appendChild(this.Text("span", AppAssistPanel.ScreenReaderOnly, AppAssistPanel.ThinkingStatus())); }
            Row.appendChild(this.Button("cancel", -1, "abtn btn secondary sm", "Cancel"));
            this.Log.appendChild(Row);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistPanel.Thinking");
        }
    };

    /**
     * The answer while it is being written: its words through the same safe renderer as a final answer
     * (text as text) - and no draft card (the words never hold one). Then "Writing…" and Cancel.
     * ⭐ Each table/chart slot (`AppAssistStream.Parts`) is "Drawing a chart…" with moving dots while it is
     * written, then - once the service sent it CLEANED - drawn by `Rich`, the final answer's own path.
     */
    private Writing = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.Partial = true;
            const Box: IAssistNode = this.StreamedAnswer();
            this.Partial = false;
            Box.className = "aa astream";
            this.Log.appendChild(Box);
            const Row: IAssistNode = this.Doc.createElement("div");
            Row.className = "athink";
            Row.setAttribute("role", "status");
            Row.appendChild(this.Text("span", "athinkline", "Writing…"));
            Row.appendChild(this.Button("cancel", -1, "abtn btn secondary sm", "Cancel"));
            this.Log.appendChild(Row);
        } catch (e) {
            this.Partial = false;
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistPanel.Writing");
        }
    };

    /**
     * `Answer` for the streamed words: the same counters (so a slot's id scope and rich budget are the ones
     * the final answer's fence N will get), words as blocks, and a slot per table/chart.
     */
    private StreamedAnswer = (): IAssistNode => {
        if (getCoreUtils().KErrorMode === true) { return this.Doc.createElement("div"); }
        try {
            const Box: IAssistNode = this.Doc.createElement("div");
            Box.className = "aa";
            this.RichSpent = 0;
            this.RichAnswer = this.Session.Entries.length;
            this.RichBlocks = 0;
            for (const Part of AppAssistStream.Parts(this.Session.Streaming, this.Session.StreamRich)) {
                if (Part.Kind === "slot") { Box.appendChild(this.Slot(Part)); continue; }
                for (const Block of AppAssistRules.Blocks(Part.Text)) { Box.appendChild(this.BlockNode(Block)); }
            }
            return Box;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistPanel.StreamedAnswer");
            return this.Doc.createElement("div");
        }
    };

    /**
     * One table/chart slot of a streamed answer. ⛔ Its markup is the SERVICE-CLEANED block, and it still goes
     * through `Rich` - cleaned again by the shared allow-list, built node by node, never parsed by the browser.
     * A note is drawn as the final answer draws it (its words); nothing at all when the answer will carry none.
     */
    private Slot = (Part: AppAssistStreamPart): IAssistNode => {
        if (getCoreUtils().KErrorMode === true) { return this.Doc.createElement("div"); }
        try {
            if (Part.Rich.Done === true && Part.Rich.Html.length > 0) { return this.Rich(Part.Rich.Html); }
            const Box: IAssistNode = this.Doc.createElement("div");
            if (Part.Rich.Done === true) {
                for (const Block of AppAssistRules.Blocks(Part.Rich.Note)) { Box.appendChild(this.BlockNode(Block)); }
                return Box;
            }
            Box.className = "aslot";
            Box.setAttribute("role", "status");
            Box.setAttribute("data-kind", Part.Rich.Kind);
            const Dots: IAssistNode = this.Text("span", "adots", "");
            Dots.setAttribute("aria-hidden", "true");
            Box.appendChild(Dots);
            Box.appendChild(this.Text("span", "aslotline", AppAssistStream.LabelFor(Part.Rich.Kind)));
            return Box;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistPanel.Slot");
            return this.Doc.createElement("div");
        }
    };

    /**
     * ⭐ HOOKS FOR OTHER TABS (`AppTabBus`, notices only - never an action): the history of this contact was
     * saved elsewhere (re-read it), or a draft's outcome was recorded elsewhere (mark it). ⛔ Neither sends.
     */
    public NoticeHistorySaved = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.LoadHistory();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistPanel.NoticeHistorySaved");
        }
    };

    public NoticeOutcome = (Id: string, Outcome: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (nz(Id).length === 0) { return; }
            for (const One of this.Session.Entries) {
                if (One.Id === Id && One.Outcome.length === 0) { One.Outcome = nz(Outcome); }
            }
            this.Render();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistPanel.NoticeOutcome");
        }
    };

    /** The tab became visible: re-read the history, at most once per `VisibleEveryMs`. */
    public OnVisible = (NowMs: number): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.LastVisibleRead > 0 && NowMs - this.LastVisibleRead < AppAssistPanel.VisibleEveryMs) { return; }
            this.LastVisibleRead = NowMs;
            this.LoadHistory();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistPanel.OnVisible");
        }
    };

    /** An element holding `Text` as TEXT. ⛔ The one way words reach the log. */
    private Text = (Tag: string, Cls: string, Text: string): IAssistNode => {
        if (getCoreUtils().KErrorMode === true) { return this.Doc.createElement(Tag); }
        try {
            const El: IAssistNode = this.Doc.createElement(Tag);
            if (Cls.length > 0) { El.className = Cls; }
            El.textContent = nz(Text);
            return El;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistPanel.Text");
            return this.Doc.createElement(Tag);
        }
    };

    private Button = (Act: string, At: number, Cls: string, Label: string): IAssistNode => {
        if (getCoreUtils().KErrorMode === true) { return this.Doc.createElement("button"); }
        try {
            const El: IAssistNode = this.Text("button", Cls, Label);
            El.setAttribute("type", "button");
            El.setAttribute(AppAssistView.ActAttr, Act);
            El.setAttribute(AppAssistView.IndexAttr, String(At));
            return El;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistPanel.Button");
            return this.Doc.createElement("button");
        }
    };

    /** DOCUMENTED EXCEPTION: builds a sentence from a name; cannot throw. */
    public static IntroFor = (Title: string): string => {
        let Who: string = "this person";
        if (AppAssistRules.IsRawId(Title) !== true) { Who = nz(Title).trim(); }
        return "Ask anything about your chat with " + Who + " - what to reply, what is still open, or a"
            + " message to send. A suggested message goes only when you press its Send.";
    };

    /**
     * Under a card whose send was accepted. ⚠ ACCEPTED IS NOT DELIVERED - the bubble in the chat
     * follows it to "Sent" (the house rule: never say "sent" for a request).
     * DOCUMENTED EXCEPTION: pure constant sentence - no input, cannot throw.
     */
    public static SentNote = (): string => {
        return "Added to the chat - its bubble shows when WhatsApp has it.";
    };

    /**
     * Under a question in flight: the moving dots and Cancel, and NO visible sentence (owner, 2026-09-28:
     * "a 2 minute feature is broken and should not be advertised" - the old long-wait line is gone).
     * Screen readers hear this one word from the row's `role="status"`, in a visually hidden span (`ScreenReaderOnly`, the app's `.sr` rule). The page still sets no timer of its own.
     * DOCUMENTED EXCEPTION: pure constant word - no input, cannot throw.
     */
    public static ThinkingStatus = (): string => {
        return "Thinking";
    };

    /** What the row says while the service is still bringing the archive back after a restart. DOCUMENTED EXCEPTION:
     *  pure constant sentence - cannot throw. */
    public static ReadyingStatus = (): string => {
        return "Getting your archive ready…";
    };

    /** The class every piece of an entry carries while its removal is in flight (`app.css`: faded, no clicks). */
    public static readonly GoingClass: string = "agoing";

    /** The app's visually hidden class (`.sr` in app.css): read by screen readers, never drawn. */
    public static ScreenReaderOnly: string = "sr";

    /** DOCUMENTED EXCEPTION: pure constant sentence - no input, cannot throw. */
    public static LoadingNote = (): string => {
        return "Loading your earlier questions…";
    };

    /** ⚠ Ink, not orange: the file is the owner's, and this says what to do. DOCUMENTED EXCEPTION: constant. */
    public static UnreadableNote = (): string => {
        return "Your earlier questions for this chat (ai-chat.json in your Dropbox) could not be read, so they"
            + " are not shown and new ones are not added to it. Restore an earlier version of that file in"
            + " Dropbox, or delete it to start again.";
    };

    /** DOCUMENTED EXCEPTION: pure constant sentence - no input, cannot throw. */
    public static FailedNote = (): string => {
        return "Your earlier questions for this chat could not be loaded just now - open the chat again to retry.";
    };

    /** DOCUMENTED EXCEPTION: pure constant sentence - no input, cannot throw. */
    public static SignInWhy = (): string => {
        return "Sign in on this browser to ask about your chats.";
    };
}
